import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { Briefcase, Wrench } from 'lucide-react';

/**
 * Dashboard View Toggle — switches the dashboard between two persona-tuned layouts.
 *
 * - CFO View: leads with $/savings/P&L; engineering metrics live below the fold.
 * - Engineer View: leads with anomalies/services/unit-economics; cash totals live below the fold.
 *
 * Choice persists in localStorage (`cartie:dashboard_view`).
 * Default = inferred from user's role (finance/admin → CFO, engineering → Engineer).
 *
 * Usage:
 *   <DashboardViewProvider defaultMode={isFinance ? 'cfo' : 'engineer'}>
 *     <DashboardViewToggle />
 *     <DashboardViewSection mode="cfo"> ...CFO-only widgets... </DashboardViewSection>
 *     <DashboardViewSection mode="engineer"> ...Eng-only widgets... </DashboardViewSection>
 *     <DashboardViewSection mode="both"> ...always-on widgets... </DashboardViewSection>
 *   </DashboardViewProvider>
 */

const STORAGE_KEY = 'cartie:dashboard_view';

const DashboardViewContext = createContext({ mode: 'cfo', setMode: () => {} });

export const useDashboardView = () => useContext(DashboardViewContext);

export const DashboardViewProvider = ({ defaultMode = 'cfo', children }) => {
  const [mode, setModeState] = useState(() => {
    try {
      const saved = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      if (saved === 'cfo' || saved === 'engineer') return saved;
    } catch (e) {
      // localStorage unavailable; fall back to default
    }
    return defaultMode === 'engineer' ? 'engineer' : 'cfo';
  });

  const setMode = useCallback((next) => {
    if (next !== 'cfo' && next !== 'engineer') return;
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // ignore
    }
  }, []);

  return (
    <DashboardViewContext.Provider value={{ mode, setMode }}>
      {children}
    </DashboardViewContext.Provider>
  );
};

/**
 * Compact pill switch — fits in dashboard headers next to other action buttons.
 */
export const DashboardViewToggle = ({ className = '' }) => {
  const { mode, setMode } = useDashboardView();

  return (
    <div
      className={`inline-flex items-center gap-0 p-0.5 rounded-full bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 ${className}`}
      data-testid="dashboard-view-toggle"
      role="tablist"
      aria-label="Dashboard view"
    >
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'cfo'}
        onClick={() => setMode('cfo')}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold tracking-tight transition-all ${
          mode === 'cfo'
            ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/30'
            : 'text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-300'
        }`}
        data-testid="dashboard-view-toggle-cfo"
        title="CFO view — lead with $, savings, P&L"
      >
        <Briefcase className="w-3.5 h-3.5" />
        <span>CFO</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'engineer'}
        onClick={() => setMode('engineer')}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold tracking-tight transition-all ${
          mode === 'engineer'
            ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/30'
            : 'text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-300'
        }`}
        data-testid="dashboard-view-toggle-engineer"
        title="Engineer view — lead with anomalies, services, unit econ"
      >
        <Wrench className="w-3.5 h-3.5" />
        <span>Engineer</span>
      </button>
    </div>
  );
};

/**
 * Conditional wrapper — only renders its children when the active view matches.
 *   mode="cfo"      → only when CFO view is active
 *   mode="engineer" → only when Engineer view is active
 *   mode="both"     → always render (rarely needed; just place outside any wrapper)
 */
export const DashboardViewSection = ({ mode: target, children }) => {
  const { mode } = useDashboardView();
  if (target === 'both') return <>{children}</>;
  if (target !== mode) return null;
  return <>{children}</>;
};

export default DashboardViewToggle;
